// The invitation endpoint serves one flat field list (`ROLE_BASED_FIELDS` plus,
// for a subcontractor, `subcontractor_profile_fields()`). A subcontractor's runs
// to forty-odd entries, which is what the design review objected to: one long
// scroll mixing who you are, what you are licensed for, which documents you hold
// and where you get paid. Grouping happens here rather than server-side because
// the wire list is shared with the mobile client and the GC-facing add form.

export interface StepAssignableField {
  name: string
  type?: string
}

export interface SignUpStep<TField extends StepAssignableField> {
  key: string
  label: string
  fields: TField[]
}

interface StepDefinition {
  key: string
  label: string
  // Server field names, in the order the step renders them. A `section_*`
  // heading is listed with the fields it introduces.
  fields: string[]
}

const ACCOUNT_STEP_KEY = "account"

// Deliberately not every field the endpoint can send: anything unlisted stays
// on the Account step, so a field added server-side shows up somewhere rather
// than silently disappearing from the form.
const STEP_DEFINITIONS: StepDefinition[] = [
  { key: ACCOUNT_STEP_KEY, label: "Account", fields: [] },
  {
    key: "business",
    label: "Business details",
    fields: [
      "specialisation",
      "location",
      "price_per_hour",
      "section_identity",
      "legal_business_name",
      "entity_type",
      "business_address",
      "business_city",
      "business_state",
      "business_zip_code",
      "ein",
      "ssn",
      // The W-9 sits with the tax id it corroborates, matching how the
      // GC-facing edit form groups its Tax & Payment section.
      "w9_document",
      "w9_validity_date",
      "primary_contact_name",
      "primary_contact_email",
      "primary_contact_phone",
      "section_cslb",
      "cslb_license_number",
      "cslb_classification",
      "cslb_status",
      "cslb_issue_date",
      "cslb_bond_amount",
      "cslb_bond_number",
      "cslb_qualifier_bond_amount",
      "cslb_qualifier_bond_number"
    ]
  },
  {
    key: "documents",
    label: "Documents",
    fields: [
      "section_compliance",
      "workers_comp_document",
      "workers_comp_expiration_date",
      "general_liability_document",
      "general_liability_expiration_date",
      "license_document",
      "license_expiration_date"
    ]
  },
  {
    key: "payment",
    label: "Payment",
    fields: [
      "section_payment",
      "payment_method",
      "ach_routing_number",
      "ach_account_number"
    ]
  }
]

const stepKeyByFieldName = new Map<string, string>(
  STEP_DEFINITIONS.flatMap((step) =>
    step.fields.map((name) => [name, step.key] as const)
  )
)

export const stepKeyForField = (name: string): string =>
  stepKeyByFieldName.get(name) ?? ACCOUNT_STEP_KEY

/**
 * Groups an invitation's fields into the steps that hold them, in each step's
 * own declared order rather than the order the server sent them.
 *
 * Only steps that actually received a field come back, so an invitation with a
 * short field list — every internal role — yields the Account step alone, which
 * the form reads as "no wizard, render as before".
 */
export const buildSignUpSteps = <TField extends StepAssignableField>(
  fields: TField[]
): SignUpStep<TField>[] => {
  const byName = new Map(fields.map((field) => [field.name, field]))
  const assigned = new Set<string>()

  const steps = STEP_DEFINITIONS.map((definition) => {
    const ordered = definition.fields
      .map((name) => {
        const field = byName.get(name)
        if (field) assigned.add(name)
        return field
      })
      .filter((field): field is TField => Boolean(field))

    return { key: definition.key, label: definition.label, fields: ordered }
  })

  const leftovers = fields.filter((field) => !assigned.has(field.name))
  const account = steps.find((step) => step.key === ACCOUNT_STEP_KEY)
  if (account) account.fields = [...account.fields, ...leftovers]

  return steps.filter(
    (step) => step.key === ACCOUNT_STEP_KEY || step.fields.length > 0
  )
}

/**
 * A heading carries no value, so a step holding nothing but headings has
 * nothing for the user to fill in and is not worth a stop of its own.
 */
export const hasEditableFields = <TField extends StepAssignableField>(
  step: SignUpStep<TField>
): boolean => step.fields.some((field) => field.type !== "section")

/**
 * The step a refused save should land on: the earliest one holding a rejected
 * field. Resolved through `stepKeyForField` rather than the built steps, so the
 * form's own password/confirm/terms fields — which never come from the server's
 * list — route to Account like anything else it does not recognise.
 */
export const findFirstErrorStepKey = <TField extends StepAssignableField>(
  steps: SignUpStep<TField>[],
  errorFieldNames: string[]
): string | undefined => {
  const erroring = new Set(errorFieldNames.map(stepKeyForField))

  return steps.find((step) => erroring.has(step.key))?.key
}
